import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import Icon, { GLYPHS } from '../../../common/icon';
import Modal from '../../../common/Modal';
import SelectInput from '../../../common/SelectInput';
import { TIME_TRACKING_ROLES } from '../../constants';
import { t } from '../../../../modules/infra/i18n';

const ROLES = Object.entries(TIME_TRACKING_ROLES).map(([key, value]) => ({ label: value.label, value: key }));

const EditTimeTrackingEntry = ({
  entry,
  startedAtHours,
  startedAtMinutes,
  endedAtHours,
  endedAtMinutes,
  role,
  onRoleChange,
  extendedHourlyWages,
  onStartedAtHoursChange,
  onStartedAtMinutesChange,
  onEndedAtHoursChange,
  onEndedAtMinutesChange,
  valid,
  duration,
  pristine,
  passcodeModalIsOn,
  onPasscodeModalClose,
  passcodeProtected,
  passcode,
  onPasscodeChange,
  confirmPasscode,
  canEditTimeTracking,
}) => (
  <div className="c-table-card__row">
    <div className="c-table-card__cell u-1/20">
      <span>
        <Icon
          glyph={GLYPHS.stopwatch}
          size="small"
          className={classnames('u-ml--', entry.active ? 'u-font-color-green' : 'u-font-color-red')}
        />
      </span>
    </div>
    <div className="c-table-card__cell u-1/10">
      <div className="layout layout--flush">
        <div className="layout__item u-3/4">
          <input
            className="c-input--dashed"
            type="number"
            disabled={!canEditTimeTracking}
            value={startedAtHours}
            onChange={onStartedAtHoursChange}
          />
        </div>
        <div className="layout__item u-1/4 zeta">{t('barber:time_tracking.edit.hh')}</div>
      </div>
    </div>
    <div className="c-table-card__cell u-1/10">
      <div className="layout layout--flush">
        <div className="layout__item u-3/4">
          <input
            className="c-input--dashed"
            type="number"
            disabled={!canEditTimeTracking}
            value={startedAtMinutes}
            onChange={onStartedAtMinutesChange}
          />
        </div>
        <div className="layout__item u-1/4 zeta">{t('barber:time_tracking.edit.mm')}</div>
      </div>
    </div>
    <div className="c-table-card__cell u-1/20" />
    <div className="c-table-card__cell u-1/10" display-if={!extendedHourlyWages} />
    <div className="c-table-card__cell u-1/10">
      <div className="layout layout--flush" display-if={!entry.active}>
        <div className="layout__item u-3/4">
          <input
            className="c-input--dashed"
            type="number"
            disabled={!canEditTimeTracking}
            value={endedAtHours}
            onChange={onEndedAtHoursChange}
          />
        </div>
        <div className="layout__item u-1/4 zeta">{t('barber:time_tracking.edit.hh')}</div>
      </div>
    </div>
    <div className="c-table-card__cell u-1/10">
      <div className="layout layout--flush" display-if={!entry.active}>
        <div className="layout__item u-3/4">
          <input
            className="c-input--dashed"
            type="number"
            disabled={!canEditTimeTracking}
            value={endedAtMinutes}
            onChange={onEndedAtMinutesChange}
          />
        </div>
        <div className="layout__item u-1/4 zeta">{t('barber:time_tracking.edit.mm')}</div>
      </div>
    </div>
    <div className="c-table-card__cell u-1/10" display-if={!extendedHourlyWages} />
    <div className="c-table-card__cell u-1/10 u-text-center">
      <div className="zeta">{duration}</div>
    </div>
    <div className="c-table-card__cell u-2/10" display-if={!!extendedHourlyWages}>
      <SelectInput
        disabled={!canEditTimeTracking}
        name="role"
        className="c-select"
        options={ROLES}
        input={{
          onChange: onRoleChange,
          value: role,
        }}
      />
    </div>
    <div className="c-table-card__cell u-2/10 u-text-center o-flexbox">
      <button
        display-if={canEditTimeTracking}
        disabled={!valid || pristine}
        className="c-btn c-btn--default c-btn--small"
        type="text"
        onClick={passcodeProtected('saveChanges')}
      >
        <span className="c-btn__text">{t('barber:time_tracking.entry.update_button')}</span>
      </button>
      <Icon
        display-if={canEditTimeTracking}
        glyph={GLYPHS.trash}
        size="small"
        className="u-inline-block u-ml- u-cursor-pointer"
        onClick={passcodeProtected('deleteEntry')}
      />
    </div>
    <Modal
      showCloseButton
      className="u-max-width-460 u-min-width-280"
      display-if={passcodeModalIsOn}
      title={t('barber:time_tracking.entry.passcode_modal_title')}
      close={onPasscodeModalClose}
    >
      <div className="layout layout--flush u-p+">
        <div className="layout__item c-label">
            {t('barber:time_tracking.add.passcode')}
        </div>
        <div className="layout__item u-6/10">
          <input
            className="c-input c-masked-input"
            type="number"
            inputMode="tel"
            autoComplete="off"
            value={passcode}
            onChange={onPasscodeChange}
          />
        </div>
        <div className="layout__item u-1/10" />
        <div className="layout__item u-3/10 text-right">
          <button className="c-btn" type="button" onClick={confirmPasscode}>
              {t('barber:time_tracking.entry.proceed_button')}
            </button>
        </div>
      </div>
    </Modal>
  </div>
);
EditTimeTrackingEntry.propTypes = {
  entry: PropTypes.object.isRequired,
  startedAtHours: PropTypes.number.isRequired,
  startedAtMinutes: PropTypes.number.isRequired,
  endedAtHours: PropTypes.number.isRequired,
  endedAtMinutes: PropTypes.number.isRequired,
  extendedHourlyWages: PropTypes.bool.isRequired,
  role: PropTypes.string.isRequired,
  onRoleChange: PropTypes.func.isRequired,
  passcode: PropTypes.string.isRequired,
  onPasscodeChange: PropTypes.func.isRequired,
  onStartedAtHoursChange: PropTypes.func.isRequired,
  onStartedAtMinutesChange: PropTypes.func.isRequired,
  onEndedAtHoursChange: PropTypes.func.isRequired,
  onEndedAtMinutesChange: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  duration: PropTypes.string.isRequired,
  passcodeModalIsOn: PropTypes.bool.isRequired,
  onPasscodeModalClose: PropTypes.func.isRequired,
  passcodeProtected: PropTypes.func.isRequired,
  confirmPasscode: PropTypes.func.isRequired,
  canEditTimeTracking: PropTypes.bool.isRequired,
};
EditTimeTrackingEntry.defaultProps = {
};

export default EditTimeTrackingEntry;
